import { useConfigStore } from "../../Stores/Config";
import { useKaiVoiceStore } from "../../Stores/KaiVoice";
import { useTicketStore } from "../../Stores/Ticket";
import "./KaiVoiceButton.scss";

const VoiceWaveIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
    <line x1="4" y1="10" x2="4" y2="14" />
    <line x1="8.5" y1="7" x2="8.5" y2="17" />
    <line x1="13" y1="4.5" x2="13" y2="19.5" />
    <line x1="17.5" y1="8" x2="17.5" y2="16" />
    <line x1="21" y1="10.5" x2="21" y2="13.5" />
  </svg>
);

/**
 * "Speak to Kai" in the composer toolbar. Renders only when the project has
 * Kai Voice enabled AND the conversation can carry a call (an active Kai/bot
 * conversation, or a fresh one the call will create).
 *
 * `newConversation` (the home hero composer) always starts a fresh
 * conversation and ignores whatever conversation happens to linger in the
 * ticket store — and gets the labelled chip, so first-time visitors discover
 * the feature. Inside a conversation the button is a bare toolbar icon like
 * the paperclip and the recorder next to it.
 */
export const KaiVoiceButton = ({ newConversation = false }: { newConversation?: boolean }) => {
  const configStore = useConfigStore();
  const voiceEnabled = useConfigStore((state) => (state.config as any)?.kaiVoice?.enabled === true);
  const callState = useKaiVoiceStore((state) => state.callState);
  const startCall = useKaiVoiceStore((state) => state.startCall);
  const currentTicket = useTicketStore((state) => state.currentTicket);
  const ticketForCall = newConversation ? undefined : currentTicket;

  if (!voiceEnabled) {
    return <></>;
  }
  // Existing non-bot conversations (already with a human) get no voice button.
  if (ticketForCall && ticketForCall.type !== "BOT") {
    return <></>;
  }
  // Mirrors the server's `ticket.bot?.active !== true` rejection: once Kai
  // has handed the thread over (or stopped), a call on it would 4xx anyway.
  if (ticketForCall && ticketForCall.bot?.active !== true) {
    return <></>;
  }
  if (callState !== "idle") {
    return <></>;
  }

  const operatorName = configStore.getBotName();
  const label = configStore.translateTextWithVarsAndFallback(
    "speakToKai",
    { operatorName },
    `Speak to ${operatorName}`,
  );
  const onClick = () => {
    startCall({ conversationShareToken: ticketForCall?.shareToken });
  };

  if (!newConversation) {
    return (
      <button
        type="button"
        className="kai-voice-button kai-voice-button--icon"
        aria-label={label}
        title={label}
        onClick={onClick}
      >
        <VoiceWaveIcon />
      </button>
    );
  }

  return (
    <button type="button" className="kai-voice-button kai-voice-button--chip" aria-label={label} onClick={onClick}>
      <VoiceWaveIcon />
      <span>{label}</span>
    </button>
  );
};
